import { useCallback, useEffect, useState } from 'react'
import { ReportViewerData } from 'reportViewer'
import { readFileContent } from './utils'

let draggingCount = 0
type Params = {
    labelRef: React.RefObject<HTMLElement>
    inputRef: React.RefObject<HTMLInputElement>
    multiple?: boolean
    handleDragging: (files: File[]) => boolean
    onDrop: (files: File[], fileData: ReportViewerData | null) => void
}
export default function useDragging({
    labelRef,
    inputRef,
    handleDragging,
    onDrop
}: Params): boolean {
    const [dragging, setDragging] = useState(false)
    const handleClick = useCallback(() => {
        inputRef.current?.click()
    }, [inputRef])

    const handleDragIn = useCallback((event: DragEvent) => {
        event.preventDefault()
        event.stopPropagation()
        draggingCount++
        if (
            event.dataTransfer?.items &&
            event.dataTransfer.items.length !== 0
        ) {
            setDragging(true)
        }
    }, [])
    const handleDragOut = useCallback((event: DragEvent) => {
        event.preventDefault()
        event.stopPropagation()
        draggingCount--
        if (draggingCount > 0) return
        setDragging(false)
    }, [])
    const handleDrag = useCallback((event: DragEvent) => {
        event.preventDefault()
        event.stopPropagation()
    }, [])
    const handleDrop = useCallback(
        async (event: DragEvent) => {
            event.preventDefault()
            event.stopPropagation()
            setDragging(false)
            draggingCount = 0

            const eventFiles = event.dataTransfer?.files
            if (eventFiles && eventFiles.length > 0) {
                const files = eventFiles[0]
                const success = handleDragging([files])

                const isJsonFile =
                    files.type === 'application/json' ||
                    files.name.toLowerCase().endsWith('.json')

                if (success && isJsonFile) {
                    try {
                        const fileData =
                            await readFileContent<ReportViewerData>(files)
                        handleDragging([files])
                        onDrop([files], fileData)
                    } catch (readError) {
                        onDrop([files], null)
                    }
                } else {
                    onDrop([files], null)
                }
            }
        },
        [handleDragging, onDrop]
    )
    useEffect(() => {
        const labelElement = labelRef.current
        labelElement?.addEventListener('click', handleClick)
        labelElement?.addEventListener('dragenter', handleDragIn)
        labelElement?.addEventListener('dragleave', handleDragOut)
        labelElement?.addEventListener('dragover', handleDrag)
        labelElement?.addEventListener('drop', handleDrop)
        return () => {
            labelElement?.removeEventListener('click', handleClick)
            labelElement?.removeEventListener('dragenter', handleDragIn)
            labelElement?.removeEventListener('dragleave', handleDragOut)
            labelElement?.removeEventListener('dragover', handleDrag)
            labelElement?.removeEventListener('drop', handleDrop)
        }
    }, [
        handleClick,
        handleDragIn,
        handleDragOut,
        handleDrag,
        handleDrop,
        labelRef
    ])

    return dragging
}
